/**
 * Magento / Adobe Commerce connection wizard.
 *
 * Three-step flow:
 *   1. Enter base URL + store code + bearer token
 *   2. Test connection (calls /rest/{store_code}/V1/store/storeViews via backend)
 *   3. Save on success
 *
 * Bearer token is never displayed after save; it travels encrypted through the
 * backend into platform_connections (AES-256-GCM).
 *
 * PRD: cross-cutting/prds/2026-06-12-I4-magento-adobe-commerce/prd.md §6
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  ShoppingCart,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import {
  testMagentoConnection,
  saveMagentoConnection,
  type TestConnectionResult,
} from '@/services/magentoIntegrationService';

export default function MagentoConnect() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const siteId = selectedSite?.id || (selectedSite as any)?._id;

  const [baseUrl, setBaseUrl] = useState('');
  const [storeCode, setStoreCode] = useState('default');
  const [bearerToken, setBearerToken] = useState('');
  const [showToken, setShowToken] = useState(false);

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [testedForSiteId, setTestedForSiteId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Reset all wizard state when the selected site changes so credentials
  // entered for one site cannot be saved to another.
  useEffect(() => {
    setBaseUrl('');
    setStoreCode('default');
    setBearerToken('');
    setShowToken(false);
    setTestResult(null);
    setTestedForSiteId(null);
    setSaved(false);
  }, [siteId]);

  if (!siteId) {
    return (
      <div className="p-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Select a site from the header to configure Magento integration.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const canTest = baseUrl.trim() !== '' && storeCode.trim() !== '' && bearerToken.trim() !== '';
  const canSave = testResult?.ok === true && testedForSiteId === siteId && !saved;

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testMagentoConnection({
        baseUrl: baseUrl.trim(),
        storeCode: storeCode.trim(),
        bearerToken: bearerToken.trim(),
      });
      setTestResult(result);
      setTestedForSiteId(siteId);
    } catch (err: any) {
      setTestResult({ ok: false, error: err.message });
      setTestedForSiteId(null);
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (testedForSiteId !== siteId) {
      toast({
        title: 'Re-test Required',
        description: 'Credentials were tested for a different site. Please re-test before saving.',
        variant: 'destructive',
      });
      setTestResult(null);
      setTestedForSiteId(null);
      return;
    }
    setSaving(true);
    try {
      await saveMagentoConnection(siteId, {
        baseUrl: baseUrl.trim(),
        storeCode: storeCode.trim(),
        bearerToken: bearerToken.trim(),
      });
      setSaved(true);
      setBearerToken('');
      toast({
        title: 'Magento Connection Saved',
        description: 'Credentials stored. You can now trigger a catalog sync.',
      });
    } catch (err: any) {
      toast({
        title: 'Save Failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingCart className="h-6 w-6" />
          Magento / Adobe Commerce
        </h1>
        <p className="text-muted-foreground mt-1">
          Connect your Magento store to sync the product catalog and enable AEO content write-back.
        </p>
      </div>

      {saved && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Connection saved. Go to <strong>Catalog Sync</strong> to run the initial full sync.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Store Credentials</CardTitle>
          <CardDescription>
            Provide your Magento REST API details. The bearer token is stored encrypted and never
            shown again after saving.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="baseUrl">Magento Base URL</Label>
            <Input
              id="baseUrl"
              placeholder="https://store.example.com"
              value={baseUrl}
              onChange={(e) => { setBaseUrl(e.target.value); setTestResult(null); setSaved(false); }}
            />
            <p className="text-xs text-muted-foreground">
              Root URL of your Magento instance — no trailing slash.
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="storeCode">Store Code</Label>
            <Input
              id="storeCode"
              placeholder="default"
              value={storeCode}
              onChange={(e) => { setStoreCode(e.target.value); setTestResult(null); setSaved(false); }}
            />
            <p className="text-xs text-muted-foreground">
              Used in REST paths: <code>/rest/{'{store_code}'}/V1/…</code>
            </p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="bearerToken">Admin Bearer Token</Label>
            <div className="relative">
              <Input
                id="bearerToken"
                type={showToken ? 'text' : 'password'}
                placeholder="Paste token from Magento Admin → System → Integrations"
                value={bearerToken}
                onChange={(e) => { setBearerToken(e.target.value); setTestResult(null); setSaved(false); }}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowToken((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showToken ? 'Hide token' : 'Show token'}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Requires: <code>Magento_Catalog::products</code> (read) + <code>Magento_Catalog::products_attributes_write</code> (write-back).
            </p>
          </div>

          {/* Test result */}
          {testResult && (
            <Alert variant={testResult.ok ? 'default' : 'destructive'}>
              {testResult.ok ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                {testResult.ok ? (
                  <span>
                    Connection successful.{' '}
                    {testResult.storeViews && testResult.storeViews.length > 0 && (
                      <>
                        Store views:{' '}
                        {testResult.storeViews.map((sv) => (
                          <Badge key={sv.code} variant="secondary" className="mr-1 text-xs">
                            {sv.code}
                          </Badge>
                        ))}
                      </>
                    )}
                  </span>
                ) : (
                  testResult.error
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={!canTest || testing}
            >
              {testing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Testing...</>
              ) : (
                'Test Connection'
              )}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave || saving}
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                'Save Connection'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
